import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBook } from '../context/BookContext';
import { useTheme } from '../context/ThemeContext';
import { StudyCompanion } from '../components/StudyCompanion';
import { useDiscoveryPrice, DiscoveryHookModal, trackDiscovery } from '../components/DiscoveryHook';
import { toast } from 'sonner';

// Puzzle sub-components
import './puzzle/puzzleStyles';
import { API_URL } from './puzzle/assets';
import { ParticleField } from './puzzle/ParticleField';
import { MapTableView } from './puzzle/MapTableView';
import { AncientScrollView } from './puzzle/AncientScrollView';
import { BookSectionView } from './puzzle/BookSectionView';
import { SpecialChapterView } from './puzzle/SpecialChapterView';
import { CompletionCelebration } from './puzzle/CompletionCelebration';
import { PuzzleMasteryDrawer } from './puzzle/PuzzleMasteryDrawer';

function PuzzleExplorer() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const theme = useTheme();
  const [pieces, setPieces] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [crossRefs, setCrossRefs] = useState([]);
  const [view, setView] = useState('map'); // 'map' | 'scroll' | 'book-section'
  const [studiedPieces, setStudiedPieces] = useState([]);
  const [progressPct, setProgressPct] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [justStudied, setJustStudied] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studyGuide, setStudyGuide] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showMastery, setShowMastery] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);
  const [activeTier, setActiveTier] = useState('beginner');
  const [showTierMenu, setShowTierMenu] = useState(false);
  const [specialChapters, setSpecialChapters] = useState([]);
  const [selectedSpecial, setSelectedSpecial] = useState(null);
  const [specialContent, setSpecialContent] = useState(null);
  const [showHookModal, setShowHookModal] = useState(false);
  const [learningProgress, setLearningProgress] = useState({});
  const [unlockedModules, setUnlockedModules] = useState([]);
  const [showCompanion, setShowCompanion] = useState(false);
  const [companionModuleKey, setCompanionModuleKey] = useState(null);
  const [curriculumLessons, setCurriculumLessons] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  const mapRef = useRef(null);
  const { selectedBook, books, selectBook } = useBook();

  const selectedPiece = selectedIndex >= 0 ? pieces[selectedIndex] : null;
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';
  const isGuest = !isAuthenticated();
  const discovery = useDiscoveryPrice();

  // Filter studied pieces to only those in the current book's pieces
  const bookStudiedPieces = useMemo(() => {
    const pieceIds = new Set(pieces.map(p => p.piece_id));
    return studiedPieces.filter(id => pieceIds.has(id));
  }, [studiedPieces, pieces]);

  // Build the complete journey steps from actual puzzle piece data
  const JOURNEY_TYPE_COLORS = { legend: '#dc2626', legend_key: '#dc2626', corner: '#1e3a5f', border: '#d97706', warning: '#ef4444', landmark: '#3b82f6', interior: '#8b5cf6' };
  const journeySteps = useMemo(() => {
    // Build from actual pieces data
    const steps = pieces.map(p => ({
      key: p.piece_id,
      label: p.title,
      color: p.color || JOURNEY_TYPE_COLORS[p.piece_type] || '#d97706',
      route: p.piece_type === 'legend_key' ? `/lesson/${p.keyword?.toLowerCase() || p.piece_id}` :
             p.piece_type === 'corner' ? `/lesson/corner${p.number}` :
             null,
      type: p.piece_type,
      pieceId: p.piece_id,
    }));
    // Add curriculum lessons if available
    if (curriculumLessons?.length > 0) {
      for (const lesson of curriculumLessons) {
        steps.push({
          key: lesson.module_key,
          label: lesson.title,
          color: JOURNEY_TYPE_COLORS[lesson.lesson_type] || '#d97706',
          route: `/lesson/${lesson.module_key}`,
          type: lesson.lesson_type,
        });
      }
    }
    return steps;
  }, [pieces, curriculumLessons]);

  // Fetch puzzle pieces from DB (tier-aware) — filtered by selected book
  useEffect(() => {
    if (!selectedBook) return;
    const bookId = selectedBook.book_id;
    (async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        let currentTier = activeTier;
        if (token) {
          const tierRes = await fetch(`${API_URL}/api/puzzle/tiers`, { headers, credentials: 'include' });
          if (tierRes.ok) {
            const tierData = await tierRes.json();
            setTierInfo(tierData);
            const tiers = ['scholar', 'berean', 'intermediate', 'beginner'];
            for (const t of tiers) {
              if (tierData.tiers?.[t]?.unlocked && tierData.tiers?.[t]?.existing_pieces > 0) {
                currentTier = t;
                setActiveTier(t);
                break;
              }
            }
          }
        }
        const bookParam = `book_id=${bookId}`;
        const piecesUrl = (token && currentTier !== 'beginner')
          ? `${API_URL}/api/puzzle/tiers/pieces/${currentTier}?${bookParam}`
          : `${API_URL}/api/puzzle/pieces?${bookParam}`;
        const res = await fetch(piecesUrl, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.pieces?.length > 0) {
            setPieces(data.pieces);
          } else {
            setPieces([]);
          }
        }
      } catch (e) { console.error('Failed to fetch pieces:', e); }
      setLoading(false);
    })();
  }, [token, selectedBook]);

  // Re-fetch pieces when user manually changes tier
  const [tierManualChange, setTierManualChange] = useState(0);
  const handleTierChange = useCallback((newTier) => {
    setActiveTier(newTier);
    setTierManualChange(prev => prev + 1);
  }, []);
  useEffect(() => {
    if (tierManualChange === 0 || isGuest || !selectedBook) return;
    (async () => {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const bookParam = `book_id=${selectedBook.book_id}`;
      const url = activeTier !== 'beginner'
        ? `${API_URL}/api/puzzle/tiers/pieces/${activeTier}?${bookParam}`
        : `${API_URL}/api/puzzle/pieces?${bookParam}`;
      const res = await fetch(url, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.pieces?.length > 0) setPieces(data.pieces);
      }
    })();
  }, [tierManualChange]);


  // Fetch progress (only for authenticated users)
  useEffect(() => {
    if (isGuest) return;
    const fetchProgress = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/mindmap/puzzle/progress`, {
          headers, credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setStudiedPieces(data.studied_pieces || []);
          setProgressPct(data.progress_pct || 0);
          setIsComplete(data.completed || false);
        }
      } catch { /* silent */ }
    };
    fetchProgress();
  }, [token]);

  // Fetch learning module progress
  useEffect(() => {
    if (!selectedBook) return;
    const fetchLearningProgress = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/learning/progress?book_id=${selectedBook.book_id}`, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setLearningProgress(data.progress || {});
          setUnlockedModules(data.unlocked || []);
        }
      } catch { /* silent */ }
    };
    fetchLearningProgress();
  }, [token, selectedBook]);

  // Fetch curriculum lessons for the journey section
  useEffect(() => {
    if (!selectedBook) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/curriculum/list?book_id=${selectedBook.book_id}`);
        if (res.ok) {
          const data = await res.json();
          setCurriculumLessons(data.lessons || []);
        }
      } catch { /* silent */ }
    })();
  }, [selectedBook]);

  // Fetch site config for landing intro
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-config/`);
        if (res.ok) {
          const data = await res.json();
          setSiteConfig(data.config || {});
        }
      } catch { /* silent */ }
    })();
  }, []);

  // Fetch AI study guide recommendations
  const fetchStudyGuide = useCallback(async () => {
    setGuideLoading(true);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/study-guide/recommend`, {
        headers, credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setStudyGuide(data);
      }
    } catch { /* silent */ }
    setGuideLoading(false);
  }, [token]);

  // Fetch discount data (only for authenticated users)
  useEffect(() => {
    if (isGuest) return;
    const fetchDiscount = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/study-guide/discount`, {
          headers, credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setDiscount(data);
        }
      } catch { /* silent */ }
    };
    fetchDiscount();
  }, [token, studiedPieces]);

  // Helper: get learning module key for a piece (book-aware)
  const getModuleKey = useCallback((piece) => {
    if (!piece) return null;
    const pt = piece.piece_type;
    const order = piece.order;
    const kw = piece.keyword;
    const isVOS = piece.book_id === '1cce7624-4873-49e1-a67b-80ddc7945345' ||
                  selectedBook?.book_id === '1cce7624-4873-49e1-a67b-80ddc7945345';
    const prefix = isVOS ? 'VOT_' : '';

    if (kw === 'GIT_101' || kw === 'GIT' || kw === 'VOT_GIT_101') return `${prefix}GIT_101`;
    if (kw === 'RECAPITULATION' || kw === 'VOT_RECAP') return isVOS ? 'VOT_RECAP' : 'RECAPITULATION';
    if (kw === 'BLUEPRINTS' || kw === 'VOT_BLUEPRINTS') return `${prefix}BLUEPRINTS`;
    if (pt === 'corner') return `${prefix}CORNER_${order}`;
    if (pt === 'border') return `${prefix}BORDER_${order}`;
    // Inner pieces with APN_ keywords map directly to the prerequisite key
    if (pt === 'inner' && kw && kw.startsWith('APN_')) return kw;
    if (isVOS) {
      if (kw) return `VOT_${kw}`;
      return `VOT_INNER_${order}`;
    }
    return `INNER_${order}`;
  }, [selectedBook]);

  const isModuleLocked = useCallback((piece) => {
    const key = getModuleKey(piece);
    if (!key) return false;
    return !unlockedModules.includes(key);
  }, [unlockedModules, getModuleKey]);

  const [companionPiece, setCompanionPiece] = useState(null);

  const openStudyCompanion = useCallback((piece) => {
    const key = getModuleKey(piece);
    if (!key) return;
    setCompanionPiece(piece);
    setCompanionModuleKey(key);
    setShowCompanion(true);
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    headers['Content-Type'] = 'application/json';
    fetch(`${API_URL}/api/learning/progress/${key}/start?book_id=${selectedBook?.book_id || ''}`, {
      method: 'POST', headers, credentials: 'include'
    }).catch(() => {});
  }, [token, getModuleKey, selectedBook]);

  const handleComprehensionAchieved = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const bookParam = selectedBook?.book_id ? `?book_id=${selectedBook.book_id}` : '';
      const res = await fetch(`${API_URL}/api/learning/progress${bookParam}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLearningProgress(data.progress || {});
        setUnlockedModules(data.unlocked || []);
      }
    } catch { /* silent */ }
  }, [token, selectedBook]);

  // Fetch cross-refs for selected piece
  useEffect(() => {
    if (!selectedPiece) { setCrossRefs([]); return; }
    (async () => {
      try {
        // Fetch appendix deeper study links from puzzle piece
        const res = await fetch(`${API_URL}/api/puzzle/pieces/${selectedPiece.piece_id}/appendices`);
        if (res.ok) {
          const data = await res.json();
          const refs = (data.appendices || []).map(a => ({
            appendix_code: a.appendix_code,
            target_book_id: a.book_id,
            description: a.chapter_title || a.title,
            target_chapter_order: 1,
            keyword: selectedPiece.keyword,
          }));
          setCrossRefs(refs);
        } else {
          setCrossRefs([]);
        }
      } catch { setCrossRefs([]); }
    })();
  }, [selectedPiece]);

  const markStudied = useCallback(async (pieceId) => {
    if (studiedPieces.includes(pieceId)) return;
    if (isGuest) {
      const piece = pieces.find(p => p.piece_id === pieceId);
      const explored = discovery.piecesExplored;
      trackDiscovery('piece_click', {
        piece_title: piece?.title || pieceId,
        pieces_explored: explored,
        source_page: 'treasure_map',
      });
      if (explored === 0) {
        discovery.recordExploration();
      } else if ((explored + 1) % 2 === 0) {
        discovery.recordExploration();
        setShowHookModal(true);
      } else {
        discovery.recordExploration();
      }
      return;
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/mindmap/puzzle/progress`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ piece_id: pieceId }),
      });
      if (res.ok) {
        const data = await res.json();
        setStudiedPieces(data.studied_pieces || [...studiedPieces, pieceId]);
        setProgressPct(data.progress_pct || 0);
        setJustStudied(pieceId);
        setTimeout(() => setJustStudied(null), 2000);
        if (data.completed && !isComplete) {
          setIsComplete(true);
          setShowCompletion(true);
        }
      }
    } catch { /* silent */ }
  }, [token, studiedPieces, isComplete]);

  const openScroll = (index) => {
    const piece = pieces[index];
    if (piece && isModuleLocked(piece)) {
      if (isGuest) {
        toast.info('Sign in to begin your guided study journey');
        navigate('/login');
        return;
      }
      openStudyCompanion(piece);
      return;
    }
    setSelectedIndex(index);
    setView('scroll');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    markStudied(piece?.piece_id);
  };

  const goToNext = () => {
    if (selectedIndex < pieces.length - 1) {
      const next = selectedIndex + 1;
      setSelectedIndex(next);
      setView('scroll');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      markStudied(pieces[next]?.piece_id);
    }
  };

  const goToPrev = () => {
    if (selectedIndex > 0) {
      const prev = selectedIndex - 1;
      setSelectedIndex(prev);
      setView('scroll');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      markStudied(pieces[prev]?.piece_id);
    }
  };

  const closeScroll = () => {
    setSelectedIndex(-1);
    setView('map');
  };

  const openBookSection = () => {
    setView('book-section');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextFromBook = () => {
    if (selectedIndex < pieces.length - 1) {
      const next = selectedIndex + 1;
      setSelectedIndex(next);
      setView(pieces[next]?.book_section_text ? 'book-section' : 'scroll');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      markStudied(pieces[next]?.piece_id);
    }
  };

  const goToPrevFromBook = () => {
    if (selectedIndex > 0) {
      const prev = selectedIndex - 1;
      setSelectedIndex(prev);
      setView(pieces[prev]?.book_section_text ? 'book-section' : 'scroll');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      markStudied(pieces[prev]?.piece_id);
    }
  };

  const corners = pieces.filter(p => p.piece_type === 'corner');
  const borders = pieces.filter(p => p.piece_type === 'border');

  // Fetch special chapters (Blueprints & Recapitulation)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/appendices/public/special-chapters`);
        if (res.ok) {
          const data = await res.json();
          setSpecialChapters(data.chapters || []);
        }
      } catch { /* silent */ }
    })();
  }, []);

  const openSpecialChapter = (chapterType) => {
    const ch = specialChapters.find(c => c.chapter_type === chapterType);
    if (ch?.linked_book_id && ch?.linked_chapter_num) {
      navigate(`/ebooks/${ch.linked_book_id}/${ch.linked_chapter_num}`);
      return;
    }
    (async () => {
      setSelectedSpecial(chapterType);
      try {
        const res = await fetch(`${API_URL}/api/admin/appendices/public/special-chapters/${chapterType}`);
        if (res.ok) {
          setSpecialContent(await res.json());
          setView('special-chapter');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (err) { console.error(err); }
    })();
  };

  const closeSpecialChapter = () => {
    setSelectedSpecial(null);
    setSpecialContent(null);
    setView('map');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgPrimary }}>
        <div className="font-serif" style={{ color: theme.navText }}>Loading the Treasure Map...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: theme.bgPrimary, color: theme.textPrimary }} data-testid="puzzle-explorer">
      {/* Background */}
      <>
        <div className="fixed inset-0 z-0">
          <img src="/images/treasure-hunter.png" alt="" className="w-full h-full object-cover" style={{ opacity: theme.name === 'dark' ? 0.18 : 0.06 }} />
          <div className="absolute inset-0" style={{ background: theme.name === 'dark'
            ? 'linear-gradient(to bottom, rgba(6,5,3,0.7), rgba(6,5,3,0.5), #060503)'
            : 'linear-gradient(to bottom, rgba(248,245,238,0.85), rgba(248,245,238,0.7), #f8f5ee)' }} />
        </div>
        {theme.name === 'dark' && <ParticleField />}
        {theme.name === 'dark' && <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[600px] h-[800px] pointer-events-none z-[1]"
          style={{ background: 'conic-gradient(from 180deg at 50% 0%, transparent 40%, rgba(245,158,11,0.04) 50%, transparent 60%)', animation: 'light-ray 4s ease-in-out infinite' }} />}
      </>

      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-4 border-b backdrop-blur-sm" style={{ borderColor: theme.borderPrimary, background: theme.bgNavbar }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-colors text-sm font-serif" style={{ color: theme.accentPrimary }} data-testid="btn-home-brand">
          <span className="text-lg">📯</span>
          <span className="hidden sm:inline">Trump of the Lord</span>
        </button>
        {books.length > 1 ? (
          <select
            value={selectedBook?.book_id || ''}
            onChange={(e) => selectBook(e.target.value)}
            className="bg-transparent border rounded-lg px-3 py-1.5 text-sm font-serif tracking-[0.15em] uppercase text-center appearance-none cursor-pointer hover:opacity-80 transition-colors"
            style={{ borderColor: theme.borderPrimary, color: theme.textSecondary }}
            data-testid="map-book-selector"
          >
            {books.map(b => (
              <option key={b.book_id} value={b.book_id} style={{ background: theme.bgPrimary, color: theme.accentPrimary }}>
                {b.title}
              </option>
            ))}
          </select>
        ) : (
          <h1 className="text-base font-serif tracking-[0.35em] uppercase" style={{ color: theme.textSecondary }}>{selectedBook?.title || 'The Treasure Map'}</h1>
        )}
        <div className="flex items-center gap-4">
          {isAdmin && (
            <button onClick={() => navigate('/admin/puzzle')} className="hover:opacity-80 transition-colors text-xs tracking-wider uppercase font-mono" style={{ color: theme.navText }} data-testid="btn-admin-puzzle">
              Edit Pieces
            </button>
          )}
          {isGuest ? (
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/git-explorer')} className="hover:opacity-80 transition-colors text-sm" style={{ color: theme.navText }} data-testid="btn-to-git">GIT Protocol</button>
              <button onClick={() => navigate('/login')} className="px-3 py-1.5 border rounded-lg hover:opacity-80 transition-colors text-sm font-medium" style={{ background: theme.accentBg, borderColor: theme.accentBorder, color: theme.accentPrimary }} data-testid="btn-to-login">Sign In</button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/help')} className="hover:opacity-80 transition-colors text-sm" style={{ color: theme.navText }} data-testid="btn-to-help">Help</button>
              <button onClick={() => navigate('/mindmap')} className="hover:opacity-80 transition-colors text-sm" style={{ color: theme.navText }} data-testid="btn-to-mindmap">Mind Map</button>
              <button onClick={() => navigate('/library')} className="hover:opacity-80 transition-colors text-sm" style={{ color: theme.navText }} data-testid="btn-to-library">Library</button>
              <button onClick={() => navigate('/streaks')} className="hover:opacity-80 transition-colors text-sm" style={{ color: theme.navText }} data-testid="btn-to-streaks">Streaks</button>
            </div>
          )}
        </div>
      </nav>

      <div className="relative z-10" ref={mapRef}>
        {view === 'map' && (
          <MapTableView corners={corners} borders={borders} pieces={pieces} onSelectPiece={openScroll}
            studiedPieces={bookStudiedPieces} progressPct={progressPct} isComplete={isComplete} justStudied={justStudied}
            studyGuide={studyGuide} guideLoading={guideLoading} showGuide={showGuide}
            onToggleGuide={() => { setShowGuide(!showGuide); if (!studyGuide && !guideLoading) fetchStudyGuide(); }}
            discount={discount}
            tierInfo={tierInfo} activeTier={activeTier} onChangeTier={handleTierChange}
            showTierMenu={showTierMenu} onToggleTierMenu={() => setShowTierMenu(!showTierMenu)}
            specialChapters={specialChapters} onOpenSpecial={openSpecialChapter} onNavigate={navigate}
            selectedBook={selectedBook} theme={theme}
            learningProgress={learningProgress} unlockedModules={unlockedModules} getModuleKey={getModuleKey}
            curriculumLessons={curriculumLessons} siteConfig={siteConfig} journeySteps={journeySteps} />
        )}
        {view === 'scroll' && selectedPiece && (
          <AncientScrollView piece={selectedPiece} crossRefs={crossRefs} onClose={closeScroll}
            onNavigate={navigate} isStudied={bookStudiedPieces.includes(selectedPiece?.piece_id)}
            onNext={selectedIndex < pieces.length - 1 ? goToNext : null}
            onPrev={selectedIndex > 0 ? goToPrev : null}
            currentIndex={selectedIndex} totalPieces={pieces.length}
            onOpenBookSection={selectedPiece.book_section_text ? openBookSection : null}
            allPieces={pieces} studiedCount={bookStudiedPieces.length} discount={discount} selectedBook={selectedBook} />
        )}
        {view === 'book-section' && selectedPiece && (
          <BookSectionView piece={selectedPiece} crossRefs={crossRefs}
            onBack={() => setView('scroll')} onNavigate={navigate}
            onNext={selectedIndex < pieces.length - 1 ? goToNextFromBook : null}
            onPrev={selectedIndex > 0 ? goToPrevFromBook : null}
            currentIndex={selectedIndex} totalPieces={pieces.length}
            studiedCount={bookStudiedPieces.length} discount={discount} selectedBook={selectedBook} />
        )}
        {view === 'special-chapter' && specialContent && (
          <SpecialChapterView chapter={specialContent} onClose={closeSpecialChapter} />
        )}
      </div>

      {showCompletion && (
        <CompletionCelebration onClose={() => setShowCompletion(false)} onNavigate={navigate} />
      )}

      {/* Discovery Hook Modal for Guests */}
      <DiscoveryHookModal
        isOpen={showHookModal && isGuest}
        onClose={() => { discovery.recordExploration(); setShowHookModal(false); }}
        currentPrice={discovery.currentPrice}
        nextPrice={discovery.nextPrice}
        savings={discovery.savings}
        retailPrice={discovery.retailPrice}
        isMaxPrice={discovery.isMaxPrice}
        piecesExplored={discovery.piecesExplored}
        sourcePage="treasure_map"
        pieceTitle={selectedPiece?.title}
        onBuy={() => { setShowHookModal(false); navigate('/buy'); }}
        onSignup={() => { discovery.recordExploration(); setShowHookModal(false); navigate('/register'); }}
      />

      {/* AI Study Companion Modal */}
      <StudyCompanion
        piece={companionPiece}
        moduleKey={companionModuleKey}
        isOpen={showCompanion}
        onClose={() => setShowCompanion(false)}
        onComprehensionAchieved={handleComprehensionAchieved}
        token={token}
      />

      {/* Puzzle Mastery Floating Button */}
      <button
        onClick={() => setShowMastery(true)}
        data-testid="btn-open-mastery"
        className="fixed bottom-20 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-2xl transition-all duration-300 hover:scale-105 border group"
        style={{
          background: 'linear-gradient(135deg, rgba(15,10,4,0.95), rgba(25,18,8,0.95))',
          borderColor: bookStudiedPieces.length === pieces.length ? 'rgba(16,185,129,0.4)' : 'rgba(217,119,6,0.25)',
          boxShadow: bookStudiedPieces.length === pieces.length
            ? '0 4px 30px rgba(16,185,129,0.2), 0 0 60px rgba(16,185,129,0.05)'
            : '0 4px 30px rgba(217,119,6,0.15), 0 0 60px rgba(217,119,6,0.05)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <div className="relative">
          <svg className="w-5 h-5 text-amber-500 group-hover:text-amber-300 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
          {bookStudiedPieces.length > 0 && (
            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold"
              style={{ background: bookStudiedPieces.length === pieces.length ? '#10b981' : '#d97706', color: '#000' }}>
              {bookStudiedPieces.length}
            </div>
          )}
        </div>
        <span className="text-xs font-serif text-amber-400/80 group-hover:text-amber-200 transition-colors hidden sm:inline">
          Puzzle Board
        </span>
      </button>

      {/* Puzzle Mastery Slide-out Drawer */}
      {showMastery && (
        <PuzzleMasteryDrawer
          pieces={pieces}
          studiedPieces={bookStudiedPieces}
          onClose={() => setShowMastery(false)}
          onSelectPiece={(idx) => { setShowMastery(false); openScroll(idx); }}
          discount={discount}
        />
      )}
    </div>
  );
}

export default PuzzleExplorer;
